import { type Pagination } from '@on3/api';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import styles from './Expanded.module.scss';

interface Button {
  page: number;
  text?: string;
}

export const ExpandedPagination = ({
  data,
}: {
  data: Pagination | undefined;
}) => {
  const router = useRouter();
  const { currentPage = 1, pageCount = 1 } = data || {};

  function handleNext() {
    router.push({
      query: { ...router.query, page: currentPage + 1 },
    });
  }

  function handleGoToPage(page: number) {
    router.push({
      query: { ...router.query, page },
    });
  }

  function handlePrevious() {
    router.push({
      query: { ...router.query, page: currentPage - 1 },
    });
  }

  // Show 3 buttons, centered around the current page
  // If there are less than 3 pages, show all pages
  const buttonsToShow: Button[] = useMemo(() => {
    const buttons: Button[] = [];

    const addPage = (page: number) => buttons.push({ page });
    const addEllipsis = (page: number) => buttons.push({ text: '...', page });

    if (pageCount <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= pageCount; i++) addPage(i);
    } else if (currentPage <= 3) {
      // Show 1, 2, 3, ..., last
      for (let i = 1; i <= 3; i++) addPage(i);
      addEllipsis(4);
      addPage(pageCount);
    } else if (currentPage <= pageCount - 3) {
      // Show 1, ..., current - 1, current, current + 1, ... last
      addPage(1);
      addEllipsis(currentPage - 2);
      for (let i = currentPage - 1; i <= currentPage + 1; i++) addPage(i);
      addEllipsis(currentPage + 2);
      addPage(pageCount);
    } else {
      // Show 1, ..., pageCount - 2, pageCount - 1, pageCount
      addPage(1);
      addEllipsis(pageCount - 3);
      for (let i = pageCount - 2; i <= pageCount; i++) addPage(i);
    }

    return buttons;
  }, [currentPage, pageCount]);

  return (
    <nav className={styles.block} data-ui="pagination">
      <button
        disabled={currentPage === 1}
        name="previous"
        onClick={handlePrevious}
        type="button"
      >
        <span className="sr-only">Previous</span>
      </button>
      {buttonsToShow.map((button) => (
        <button
          className={clsx({ [styles.active]: button.page === currentPage })}
          key={button.page}
          onClick={() => handleGoToPage(button.page)}
          type="button"
        >
          {button.text || button.page}
        </button>
      ))}
      <button
        disabled={currentPage === pageCount}
        name="next"
        onClick={handleNext}
        type="button"
      >
        <span className="sr-only">Next</span>
      </button>
    </nav>
  );
};
